/**
 * PostHog Cloud EU, SANS COOKIE (ROADMAP 9.1, §11).
 *
 * ══ Ce que « sans cookie » veut dire ici, exactement ═════════════════════════
 *
 * `persistence: 'memory'` : PostHog n'écrit **rien**. Ni cookie, ni `localStorage`, ni
 * `sessionStorage`. C'est ce qui dispense de bannière, et c'est la contrepartie du §11.
 *
 * ⚠️ **Et ça a un coût qu'il faut connaître** : sans stockage, chaque chargement de page
 * serait une personne différente, donc aucune rétention mesurable. On donne donc à PostHog
 * l'identifiant que le JEU possède déjà — l'`auth.uid()` de la session anonyme Supabase,
 * qui existe pour sauvegarder la ville et existerait sans analytics. Aucun stockage n'est
 * créé pour la mesure ; on réutilise celui qui est strictement nécessaire au service.
 *
 * C'est la forme « mesure d'audience » que la CNIL exempte de consentement : pas de suivi
 * entre sites, pas de partage, pas de recoupement, et une finalité unique. **Décision à
 * revoir avec Benoit si le périmètre change** — le jour où l'on voudrait suivre un joueur
 * d'un jeu à l'autre, cette exemption tombe.
 *
 * ══ Trois règles que ce module ne viole jamais ══════════════════════════════
 *
 * 1. **Il ne casse jamais le jeu.** Pas de clé, réseau coupé, bloqueur de pub : tout
 *    échoue en silence. Une mesure qui empêche de jouer est pire que pas de mesure.
 * 2. **Il ne bloque jamais le démarrage.** Le module est chargé à la DEMANDE, après le
 *    premier rendu : le §1 promet un jeu jouable en moins de cinq secondes, et une
 *    bibliothèque d'analytics dans le chemin critique est le contraire de ça.
 * 3. **Il ne collecte que ce qu'on a nommé** (`events.ts`) : ni autocapture, ni
 *    enregistrement de session, ni frappe clavier.
 */

import type { EventName, EventProps } from './events';

const KEY = import.meta.env.VITE_POSTHOG_KEY ?? '';
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com';

/** Vrai si la mesure est configurée. Sans clé, tout ce module est un no-op. */
export function isAnalyticsConfigured(): boolean {
  return KEY !== '';
}

interface Client {
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string) => void;
}

let client: Client | null = null;
let loading: Promise<Client | null> | null = null;

/**
 * Événements émis AVANT que la bibliothèque soit prête.
 *
 * Le premier d'entre eux est `game_started`, qui est justement le dénominateur de tout
 * l'entonnoir : le perdre reviendrait à mesurer des pourcentages sans savoir de quoi. La
 * file est bornée — un jeu qui tourne une heure sans réseau ne doit pas accumuler des
 * milliers d'événements en mémoire pour les envoyer d'un coup.
 */
const queue: { event: EventName; props: EventProps }[] = [];
const QUEUE_MAX = 50;

/**
 * Charge PostHog, une seule fois, sans jamais faire échouer l'appelant.
 *
 * L'import est DYNAMIQUE : la bibliothèque sort du bundle principal et n'est demandée
 * qu'après le premier rendu. Mesuré ailleurs dans ce projet et vrai ici aussi — ce qui
 * n'est pas dans le chemin critique ne doit pas y entrer.
 */
async function load(): Promise<Client | null> {
  if (!isAnalyticsConfigured()) return null;
  if (client !== null) return client;

  loading ??= (async () => {
    try {
      const { default: posthog } = await import('posthog-js');

      posthog.init(KEY, {
        api_host: HOST,
        /*
          ⚠️ Les quatre lignes qui font le « sans cookie ». Aucune n'est optionnelle :
          retirer `persistence` remet des cookies, retirer `autocapture` remet la collecte
          de tous les clics, et `disable_session_recording` évite d'enregistrer l'écran.
        */
        persistence: 'memory',
        autocapture: false,
        capture_pageview: false,
        disable_session_recording: true,
        // On envoie nos propres pages : le jeu et la vitrine publique sont deux mondes,
        // et une `pageview` automatique les confondrait.
        capture_pageleave: false,
        /*
          ⚠️ TROIS MODULES QUE POSTHOG CHARGE DE LUI-MÊME, et qu'il faut éteindre nommément.

          Mesuré : sans ces lignes, le navigateur allait chercher `surveys.js`,
          `dead-clicks-autocapture.js` et `exception-autocapture.js` — trois requêtes et
          trois collectes que personne n'avait demandées. « Ni autocapture, ni
          enregistrement de session » ne suffit pas à les couvrir : ce sont des options
          distinctes, activées par défaut.

          Les sondages en particulier peuvent AFFICHER quelque chose au joueur. Une
          bibliothèque de mesure n'a rien à dessiner dans ce jeu.
        */
        disable_surveys: true,
        capture_dead_clicks: false,
        capture_exceptions: false,
        // Les cartes de chaleur enregistrent la position de chaque clic sur la page :
        // c'est de l'autocapture sous un autre nom, et elle démarre par défaut.
        enable_heatmaps: false,
      });

      client = posthog;
      for (const pending of queue.splice(0)) {
        client.capture(pending.event, pending.props);
      }
      return client;
    } catch {
      // Bloqueur de pub, réseau coupé, CDN indisponible : on ne mesure pas, et le jeu
      // ne s'en aperçoit pas. C'est la règle 1 de ce module.
      return null;
    }
  })();

  return loading;
}

/**
 * Démarre la mesure et rattache la session au joueur.
 *
 * `playerId` est l'`auth.uid()` de la session anonyme — voir l'en-tête. Passer `null` est
 * légitime : sans configuration Supabase, il n'y a pas d'identité, et la mesure reste
 * anonyme pour de bon.
 */
export function startAnalytics(playerId: string | null): void {
  void load()
    .then((ready) => {
      if (ready === null || playerId === null) return;
      ready.identify(playerId);
    })
    .catch(() => undefined);
}

/**
 * Émet un événement. Ne rend rien, ne lève rien, n'attend rien.
 *
 * ⚠️ Volontairement SYNCHRONE côté appelant : un `await` sur une mesure finirait par
 * retarder un geste de jeu le jour où quelqu'un l'ajoute par réflexe.
 */
export function track(event: EventName, props: EventProps = {}): void {
  if (!isAnalyticsConfigured()) return;

  if (client === null) {
    if (queue.length < QUEUE_MAX) queue.push({ event, props });
    void load();
    return;
  }

  try {
    client.capture(event, props);
  } catch {
    // Voir la règle 1.
  }
}

/** Remet le module à zéro. Réservé aux tests — le jeu n'a aucune raison de l'appeler. */
export function resetAnalyticsForTests(): void {
  client = null;
  loading = null;
  queue.length = 0;
}
